import React, { useState, useEffect } from 'react';
import { FileCheck, Scale } from 'lucide-react';
import { EmbeddedAIWorkspace } from '../legal-ai';
import {
  Contract,
  ReviewClause,
  NegotiationPoint,
  ContractSummary,
  ContractAnalysisJSON
} from '../../../types/contract.types';
import analysisService from '../../../services/analysisService';
import { decodeHtmlEntities } from '../../../utils/textUtils';

interface ContractReviewProps {
  contracts: Contract[];
  reviewClauses: ReviewClause[];
  negotiationPoints: NegotiationPoint[];
  currentUser: string;
  initialContractId?: string;
  onAnalysisUpload: (contractId: string, file: File) => Promise<void>;
  onSummaryUpdate: (contractId: string, summary: Partial<ContractSummary>) => void;
  onAddClause: (contractId: string, clause: Omit<ReviewClause, 'clauseId' | 'reviewDate' | 'negotiationHistory'>) => void;
  onUpdateClause: (clauseId: string, updates: Partial<ReviewClause>) => void;
  onDeleteClause: (clauseId: string) => void;
  onAddNegotiationPoint: (point: Omit<NegotiationPoint, 'id' | 'createdDate' | 'lastModified'>) => void;
  onUpdateNegotiationPoint: (id: string, updates: Partial<NegotiationPoint>) => void;
  onDeleteNegotiationPoint: (id: string) => void;
}

export const ContractReview: React.FC<ContractReviewProps> = ({
  contracts,
  initialContractId,
}) => {
  const [selectedContractId, setSelectedContractId] = useState<string | null>(
    initialContractId || (contracts.length > 0 ? contracts[0].id : null)
  );
  const [jsonAnalysis, setJsonAnalysis] = useState<ContractAnalysisJSON | null>(null);
  const [analysisId, setAnalysisId] = useState<string | null>(null);

  const selectedContract = contracts.find(c => c.id === selectedContractId);

  // Update selectedContractId when initialContractId prop changes
  React.useEffect(() => {
    if (initialContractId && initialContractId !== selectedContractId) {
      setSelectedContractId(initialContractId);
    }
  }, [initialContractId, selectedContractId]);

  // Load analysis from database when contract changes
  useEffect(() => {
    const loadAnalysisFromDatabase = async () => {
      if (!selectedContractId) {
        setJsonAnalysis(null);
        return;
      }

      try {
        // Find the contract to get the actual MongoDB ObjectId
        const contract = contracts.find(c => c.id === selectedContractId);
        const actualContractId = contract?._id;

        if (!actualContractId) {
          console.log('No ObjectId found for contract:', selectedContractId);
          setJsonAnalysis(null);
          return;
        }

        // Check if analysis exists in database using ObjectId
        const existsResult = await analysisService.checkAnalysisExists(actualContractId);
        if (existsResult.exists) {
          // Load analysis from database using ObjectId
          const analysisResult = await analysisService.getAnalysis(actualContractId);
          if (analysisResult.data?.analysisData) {
            // Store the analysis ID for database operations
            setAnalysisId(analysisResult.data._id);

            // Convert the stored analysis data back to ContractAnalysisJSON format
            const analysisData = analysisResult.data.analysisData;
            const contractAnalysis: any = {
              executive_summary: {
                overview: analysisData.executive_summary?.overview || '',
                overall_favorability_score: analysisData.executive_summary?.overall_favorability_score || 5,
                critical_issues: analysisData.executive_summary?.key_concerns?.map((concern: string, index: number) => ({
                  title: concern,
                  description: `Critical concern identified: ${concern}`,
                  risk_level: 'High' as const,
                  icon: 'alert-triangle',
                  recommended_action: analysisData.executive_summary?.recommendations?.[index] || 'Review and address this concern'
                })) || [] as any
              },
              clauses: analysisData.clauses?.map((clause: any) => ({
                reference: clause.id,
                title: clause.title,
                party_obligations: clause.party_obligations || clause.content,
                summary: clause.summary || clause.analysis,
                full_heading: clause.subcategory || '',
                problematic_subclauses: [],
                risk_assessment: {
                  legal_risk: clause.risk_level === 'high' ? 'High legal exposure' : 'Standard legal risk',
                  financial_exposure: clause.risk_level === 'high' ? 'Significant financial risk' : 'Limited financial exposure',
                  operational_impact: clause.risk_level === 'high' ? 'Major operational concerns' : 'Minor operational impact',
                  regulatory_concerns: clause.risk_level === 'high' ? 'Potential regulatory issues' : 'No major regulatory concerns'
                },
                negotiation_analysis: {
                  favorability: 'balanced' as const,
                  market_standard: 'Standard market terms',
                  recommended_modification: clause.summary || 'Review this clause',
                  classification: 'negotiable' as const
                },
                actionable_recommendations: {
                  immediate_actions: clause.summary || 'Review this clause',
                  approvals_needed: 'Legal review required',
                  implementation_notes: 'Standard implementation'
                },
                icons: {
                  risk: clause.risk_level === 'high' ? 'alert-triangle' : 'alert-circle',
                  negotiation: 'message-circle',
                  finance: 'dollar-sign',
                  action: 'check-circle'
                }
              })) || [] as any
            };
            setJsonAnalysis(contractAnalysis as any);
          } else {
            setJsonAnalysis(null);
            setAnalysisId(null);
          }
        } else {
          setJsonAnalysis(null);
          setAnalysisId(null);
        }
      } catch (error) {
        console.error('Error loading analysis from database:', error);
        setJsonAnalysis(null);
        setAnalysisId(null);
      }
    };

    loadAnalysisFromDatabase();
  }, [selectedContractId, contracts]);

  if (contracts.length === 0) {
    return (
      <div className="text-center py-12">
        <FileCheck className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Contracts Available</h3>
        <p className="text-gray-500 dark:text-gray-400">Add contracts to start the review process.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Contract Selector */}
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border p-5">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Contract for Review
        </label>
        <select
          value={selectedContractId || ''}
          onChange={(e) => setSelectedContractId(e.target.value)}
          className="w-full px-4 py-3 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 focus:border-primary-500 dark:focus:border-accent-400"
        >
          {contracts.map(contract => (
            <option key={contract.id} value={contract.id}>
              {decodeHtmlEntities(contract.contractTitle)} - {contract.supplier}
            </option>
          ))}
        </select>
      </div>

      {/* Legal AI Assistant - Direct Display */}
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border overflow-hidden">
        {selectedContract && (
          <EmbeddedAIWorkspace
            contract={selectedContract}
            jsonAnalysis={jsonAnalysis}
            analysisId={analysisId || undefined}
          />
        )}
      </div>
    </div>
  );
};
